import React, { useMemo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Multiselect, IMultiselectValueLabelPair } from 'ui/Multiselect';
import { getCountryCodeForCurrency } from 'utils/currencyConversion';
import Flag from 'react-world-flags';
import { useCurrencyConversion } from 'context/CurrencyConversion/CurrencyConversionContext';
import { makeBackendApi } from 'services/BackendApi';
import { enqueueNotification } from 'store/modules/ui';
import { iActingOnBehalfOfUser } from 'store/modules/actingOnBehalfOf/model';

const CurrencyFlag = ({ currencyCode, className }: { currencyCode: string; className?: string }) => {
  const countryCode = getCountryCodeForCurrency(currencyCode);
  if (!countryCode) {
    return <span className={`inline-block w-[18px] text-center ${className || ''}`}>•</span>;
  }
  return <Flag code={countryCode} className={`w-[18px] h-[13px] inline-block ${className || ''}`} />;
};

interface IBasketCurrencySelectorProps {
  basketBuildUuid: string;
  actingOnBehalfOfUser?: iActingOnBehalfOfUser;
  selectedTaUserUuid?: string | null;
  onPaymentCurrencyUpdated?: (newCurrency: string | null) => void;
}

export const BasketCurrencySelector = ({
  basketBuildUuid,
  actingOnBehalfOfUser,
  selectedTaUserUuid,
  onPaymentCurrencyUpdated,
}: IBasketCurrencySelectorProps) => {
  const dispatch = useDispatch();
  const { sortedCurrencies, selectedCurrencyData, onCurrencyChange } = useCurrencyConversion();
  const [isUpdating, setIsUpdating] = useState(false);

  const options: IMultiselectValueLabelPair[] = useMemo(() => {
    return sortedCurrencies.map(c => ({
      value: c.to.code,
      label: `${c.to.code} - ${c.to.name}`,
      iconHtml: <CurrencyFlag currencyCode={c.to.code} className="mr-[8px]" />,
      iconAlign: 'left' as const,
    }));
  }, [sortedCurrencies]);

  const selectedValues = selectedCurrencyData ? [selectedCurrencyData.to.code] : [];

  const handleUpdate = useCallback(
    async (newSelectedValues: string[]) => {
      const newCode = newSelectedValues[0] || null;
      const previousCode = selectedCurrencyData?.to.code || null;

      // Optimistically update the UI
      onCurrencyChange(newCode);
      setIsUpdating(true);

      try {
        const backendApi = makeBackendApi(selectedTaUserUuid as string | undefined);
        await backendApi.patchBasketBuildPaymentCurrency(basketBuildUuid, newCode, actingOnBehalfOfUser);
        onPaymentCurrencyUpdated?.(newCode);
      } catch {
        // Revert on failure
        onCurrencyChange(previousCode);
        dispatch(enqueueNotification({ message: 'Failed to update payment currency. Please try again.', options: { variant: 'error' } }));
      } finally {
        setIsUpdating(false);
      }
    },
    [selectedCurrencyData, onCurrencyChange, basketBuildUuid, actingOnBehalfOfUser, selectedTaUserUuid, dispatch, onPaymentCurrencyUpdated]
  );

  const selectedValuesRenderer = useCallback((label: string) => {
    return label.split(' - ')[0];
  }, []);

  if (!sortedCurrencies.length) {
    return null;
  }

  return (
    <div className={`relative ${isUpdating ? 'opacity-50 pointer-events-none' : ''}`}>
      <Multiselect
        className="bg-ivory w-[150px]"
        itemsClassname="bg-ivory !w-[250px] right-0 left-auto"
        itemCtaClassName="hover:bg-gray-10"
        options={options}
        selectedValues={selectedValues}
        onUpdate={handleUpdate}
        isSingleSelectMode
        hideCheckboxes
        isCloseOnSelect
        isIncludeClearButton
        placeholderText="Currency"
        selectedValuesRenderer={selectedValuesRenderer}
        showSelectedIcon
      />
    </div>
  );
};
